/**
 * Color variant cards — prices via ?view=po-info-badge (same as PLP), one fetch per variant.
 */
class PoVariantColorCards extends HTMLElement {
  connectedCallback() {
    this.template = this.querySelector('[data-card-template]');
    this.container = this.querySelector('[data-js-cards-container]');
    this._lastProducts = [];
    this._priceCache = new Map();
    this._priceInflight = new Map();

    this.titleEl = this.closest('.tw-po-modal-content')?.querySelector('.tw-po-modal-title');
    this.title = this.titleEl?.textContent || '';

    this._onGraphLoaded = (event) => {
      const products = (event.detail && event.detail.products) || [];
      this.render(products);
      this.updateModalTitle(products.length);
    };

    this._onDkCodesChanged = () => {
      this._priceCache.clear();
      this.hydrateAllCardPrices();
    };

    window.addEventListener('variantsHandleGraphLoaded', this._onGraphLoaded);
    document.addEventListener('po:dk:discount-codes-changed', this._onDkCodesChanged);

    if (window.variantsHandleGraphData?.loaded) {
      this._onGraphLoaded({
        detail: { products: window.variantsHandleGraphData.products || [] },
      });
    }

    [400, 1200].forEach((ms) => {
      window.setTimeout(() => {
        this._priceCache.clear();
        this.hydrateAllCardPrices();
      }, ms);
    });
  }

  disconnectedCallback() {
    window.removeEventListener('variantsHandleGraphLoaded', this._onGraphLoaded);
    document.removeEventListener('po:dk:discount-codes-changed', this._onDkCodesChanged);
    this._priceInflight.forEach((controller) => controller.abort());
    this._priceInflight.clear();
  }

  updateModalTitle(qty) {
    if (!this.titleEl) return;
    this.titleEl.textContent = this.title.replace('#', String(qty + 1));
  }

  getDkCodesQuery() {
    const csv =
      (typeof window.PoDkStorefrontPrices?.getAppliedCodesCsv === 'function'
        ? window.PoDkStorefrontPrices.getAppliedCodesCsv()
        : '') ||
      document.documentElement?.dataset?.poDkAppliedCodes ||
      document.querySelector('po-variant-handles-products')?.dataset?.dkAppliedCodes ||
      '';

    const codes = csv.split(',').map((c) => c.trim()).filter(Boolean);
    return codes.length ? `&po_dk_codes=${encodeURIComponent(codes.join(','))}` : '';
  }

  render(products) {
    if (!this.template || !this.container || !products.length) return;

    this._lastProducts = products;
    this.container.innerHTML = '';

    for (let i = 0; i < products.length; i++) {
      const product = products[i];
      const clone = this.template.content.cloneNode(true);
      const link = clone.querySelector('[data-card-link]');

      link.href = PoVariantColorCards.productPageUrl(product);
      link.dataset.productHandle = product.handle || '';
      link.title = product.title;

      const color = product.color ? product.color.value : '';
      const lens = product.lensColorDescription ? product.lensColorDescription.value : '';
      link.dataset.mocoAltBase = link.dataset.mocoAltBase || '';
      const mocoAlt = link.dataset.mocoAltBase
        .replace('{frame}', color)
        .replace('{lenses}', lens);

      if (product.featuredImage) {
        const baseUrl = product.featuredImage.url;
        const image = clone.querySelector('[data-card-image]');
        image.src = PoVariantColorCards.getSizedImageUrl(baseUrl, 400);
        image.alt = mocoAlt;

        clone.querySelector('[data-card-source-tb]').srcset =
          PoVariantColorCards.getSizedImageUrl(baseUrl, 768);
        clone.querySelector('[data-card-source-sd]').srcset =
          PoVariantColorCards.getSizedImageUrl(baseUrl, 2000);
        clone.querySelector('[data-card-source-xlg]').srcset =
          PoVariantColorCards.getSizedImageUrl(baseUrl, 2000);
      }

      clone.querySelector('[data-card-available]').setAttribute('data-card-available', product.availableForSale);

      if (color) {
        const frameBlock = clone.querySelector('[data-card-frame]');
        frameBlock.hidden = false;
        frameBlock.querySelector('[data-card-frame-value]').innerHTML =
          PoVariantColorCards.formatDescription(color);
      }

      if (lens) {
        const lensBlock = clone.querySelector('[data-card-lens]');
        lensBlock.hidden = false;
        lensBlock.querySelector('[data-card-lens-value]').innerHTML =
          PoVariantColorCards.formatDescription(lens);
      }

      if (!color && !lens) {
        const titleEl = clone.querySelector('[data-card-title]');
        titleEl.hidden = false;
        titleEl.textContent = product.title;
      }

      PoVariantColorCards.setPlaceholderPrice(clone, product);

      this.container.appendChild(clone);
    }

    this.hydrateAllCardPrices();
  }

  hydrateAllCardPrices() {
    if (!this.container) return;

    this.container.querySelectorAll('a[data-card-link][data-product-handle]').forEach((anchor) => {
      const productUrl = PoVariantColorCards.productPageUrl({ handle: anchor.dataset.productHandle });
      const priceHost = anchor.querySelector('[data-po-price]');
      if (productUrl && priceHost) {
        this.hydratePriceFromServer(productUrl, priceHost);
      }
    });
  }

  async hydratePriceFromServer(productUrl, priceHost) {
    const cacheKey = `${productUrl}${this.getDkCodesQuery()}`;
    const cached = this._priceCache.get(cacheKey);

    if (cached) {
      PoVariantColorCards.applyFetchedPrice(priceHost, cached);
      return;
    }

    const prev = this._priceInflight.get(priceHost);
    if (prev) prev.abort();

    const controller = new AbortController();
    this._priceInflight.set(priceHost, controller);

    try {
      const response = await fetch(
        `${productUrl}?view=po-info-badge${this.getDkCodesQuery()}`,
        { signal: controller.signal }
      );

      if (!response.ok) return;

      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const fetched = PoVariantColorCards.extractPriceFromPoInfoBadge(doc);

      if (!fetched || !priceHost.isConnected) return;

      this._priceCache.set(cacheKey, fetched.cloneNode(true));
      PoVariantColorCards.applyFetchedPrice(priceHost, fetched);
    } catch (error) {
      if (error?.name !== 'AbortError') {
        /* keep placeholder */
      }
    } finally {
      if (this._priceInflight.get(priceHost) === controller) {
        this._priceInflight.delete(priceHost);
      }
    }
  }

  /**
   * po-info-badge wraps markup in <template> (same as PLP po-plp-product-card).
   * querySelector on the document does not see inside <template>.
   */
  static extractPriceFromPoInfoBadge(doc) {
    const templateHtml = doc.querySelector('template')?.innerHTML?.trim();
    const scope = document.createElement('template');

    if (templateHtml) {
      scope.innerHTML = templateHtml;
    } else {
      scope.innerHTML = doc.body?.innerHTML || '';
    }

    return (
      scope.content.querySelector('.dinamic-price [data-po-price]') ||
      scope.content.querySelector('[data-price-wrapper] [data-po-price]') ||
      scope.content.querySelector('[data-po-price]')
    );
  }

  static applyFetchedPrice(priceHost, fetchedNode) {
    if (!priceHost || !fetchedNode) return;

    priceHost.innerHTML = fetchedNode.innerHTML;

    for (const attr of fetchedNode.attributes) {
      if (attr.name === 'class') {
        priceHost.className = attr.value;
      } else if (attr.name.startsWith('data-')) {
        priceHost.setAttribute(attr.name, attr.value);
      }
    }
  }

  static productPageUrl(product) {
    const handle = product?.handle;
    if (!handle) return product?.url || '';

    const root = window.Shopify?.routes?.root || '/';
    const origin = window.location?.origin || '';
    return `${origin}${root}products/${handle}`;
  }

  static setPlaceholderPrice(clone, product) {
    const priceEl = clone.querySelector('[data-card-price]');
    const variant = product?.variants?.nodes?.[0];
    if (!priceEl || !variant?.price) return;

    priceEl.textContent = PoVariantColorCards.formatPrice(
      parseFloat(variant.price.amount),
      variant.price.currencyCode
    );
  }

  static formatPrice(amount, currencyCode) {
    const value = parseFloat(amount);
    if (isNaN(value)) return '';

    try {
      return new Intl.NumberFormat(window.Shopify?.locale || 'en', {
        style: 'currency',
        currency: currencyCode,
      }).format(value);
    } catch {
      return `${currencyCode} ${value.toFixed(2)}`;
    }
  }

  static getSizedImageUrl(url, width) {
    if (!url) return '';
    try {
      const u = new URL(url, location.origin);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch {
      return url;
    }
  }

  static formatDescription(text) {
    const words = text.split(' ');
    const secondWord = words.length > 1 ? words[1].trim() : '';

    if (text.length > 12 && words.length >= 3 && secondWord.length >= 4) {
      return `${words[0]} ${words[1]}<br>${words.slice(2).join(' ')}`;
    }

    return text;
  }
}

customElements.define('po-variant-color-cards', PoVariantColorCards);
